import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import { userFacingStrategyFetchError, userFacingCurrentResultsError } from '../../utils/userFacingStrategyErrors';
import {
  MULTI_STRAT_SHEET_NAME,
  isMultiStratName,
} from '../../utils/vectorCapitalScenario';

const TTL_MS = 60 * 1000;

const memoryCache = new Map();
const inflight = new Map();

function buildCacheKey(kind, key) {
  return `${kind}::${key}`;
}

async function fetchAndCache(cacheKey, url, normalizeError = userFacingStrategyFetchError) {
  if (inflight.has(cacheKey)) return inflight.get(cacheKey);

  const promise = (async () => {
    const response = await fetch(url, { headers: getBearerAuthHeaders() });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      const msg = normalizeError(data?.error || `Request failed (${response.status})`);
      const error = new Error(msg);
      error.status = response.status;
      throw error;
    }
    return data;
  })();

  inflight.set(cacheKey, promise);
  try {
    const data = await promise;
    memoryCache.set(cacheKey, { ts: Date.now(), data });
    return data;
  } finally {
    inflight.delete(cacheKey);
  }
}

function readFresh(cacheKey) {
  const hit = memoryCache.get(cacheKey);
  if (!hit) return null;
  if (Date.now() - hit.ts > TTL_MS) return null;
  return hit.data;
}

async function getMultiStratRows({ email }) {
  // Fetch the already-combined Vector Master Strat rows from the server, which
  // builds them from the ADMIN-EDITABLE composition (Sheet Manager / DB), not a
  // hardcoded source list. This keeps the dashboard widget in sync with whatever
  // the Sheet Manager defines — so renamed/changed source sheets work here too.
  const cacheKey = buildCacheKey('strategy-sheet', `${MULTI_STRAT_SHEET_NAME}::${email || ''}`);
  const cached = readFresh(cacheKey);
  if (cached) return cached;
  const url = API_ENDPOINTS.strategySheet(MULTI_STRAT_SHEET_NAME, email || undefined);
  return fetchAndCache(cacheKey, url);
}

async function getCustomMultistratRows({ comboId, email }) {
  if (!comboId) throw new Error('Custom combo id is required');
  // Cache key includes email since the endpoint is auth-scoped — different
  // users have different combos under the same id space.
  const cacheKey = buildCacheKey('custom-combo', `${comboId}::${email || ''}`);
  const cached = readFresh(cacheKey);
  if (cached) return cached;
  const url = API_ENDPOINTS.customMultistratData(comboId);
  return fetchAndCache(cacheKey, url);
}

export async function getStrategySheet({ strategy, email }) {
  if (!strategy) throw new Error('Strategy is required');
  // Accept both the current "Vector Master Strat" name and the legacy
  // "Multi-Strat" alias so old saved widget configs keep working.
  if (isMultiStratName(strategy)) {
    return getMultiStratRows({ email });
  }
  // Custom Multi-Strat combo — internal value is `custom:<id>`. Server
  // returns the same {success, rows} shape as /api/strategy-sheet so the
  // rest of the widget pipeline works unchanged.
  if (typeof strategy === 'string' && strategy.startsWith('custom:')) {
    const comboId = strategy.slice('custom:'.length);
    return getCustomMultistratRows({ comboId, email });
  }
  const cacheKey = buildCacheKey('strategy-sheet', `${strategy}::${email || ''}`);
  const cached = readFresh(cacheKey);
  if (cached) return cached;
  const url = API_ENDPOINTS.strategySheet(strategy, email || undefined);
  return fetchAndCache(cacheKey, url);
}

export async function getCurrentResults({ email }) {
  const cacheKey = buildCacheKey('current-results', email || '');
  const cached = readFresh(cacheKey);
  if (cached) return cached;
  const url = API_ENDPOINTS.currentResults(email || undefined);
  return fetchAndCache(cacheKey, url, userFacingCurrentResultsError);
}

export function invalidateStrategyDataCache() {
  memoryCache.clear();
  inflight.clear();
}
